import { Injectable, inject, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '@app/core/services/environment.service';
import { ClaimsService } from '@services/claims.service';
import { catchError, filter, take } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';

const TRID_SESSION_KEY = 'we_trid';
// Cookie registered in CCM19 under "Functional" category.
// This cookie acts as a consent signal — when accepted, we are allowed to
// generate and store the user's tracking ID (trid) in sessionStorage.
const TRID_CONSENT_COOKIE = 'we_trid_consent';

interface TridResponse {
	trid: string;
}

/**
 * Declares the global CCM19 API available after the script loads.
 */
declare global {
	interface Window {
		CCM?: {
			fullConsentGiven?: boolean;
			acceptedCookies?: ({ name?: string; [key: string]: unknown } | string)[];
			acceptedEmbeddings?: unknown[];
			consent?: Record<string, unknown>;
		};
	}
}

/**
 * Service that listens for CCM19 cookie consent changes.
 * When functional cookies are accepted AND user registration data exists,
 * it fetches or generates a tracking ID (trid) and stores it in sessionStorage.
 *
 * The trid flow requires BOTH conditions:
 * 1. User has accepted the functional consent cookie in CCM19
 * 2. User has a registration row (user_registration_data exists)
 *
 * Either condition can happen first — the flow triggers when both are met.
 */
@Injectable({
	providedIn: 'root',
})
export class CookieConsentService implements OnDestroy {
	private readonly http = inject(HttpClient);
	private readonly environmentService = inject(EnvironmentService);
	private readonly claimsService = inject(ClaimsService);
	private readonly ngZone = inject(NgZone);

	private initialized = false;
	private eventHandler: (() => void) | null = null;
	private registrationSub: Subscription | null = null;

	/**
	 * Initialize the CCM19 consent listener.
	 * Call this once on app init.
	 */
	initialize(): void {
		if (this.initialized) return;
		this.initialized = true;

		// Listen for CCM19 consent change events
		// TODO: Verify the correct event name from CCM19 implementation
		this.eventHandler = () => {
			this.ngZone.run(() => this.attemptTridFlow());
		};

		window.addEventListener('ccm19WidgetClosed', this.eventHandler);

		// Subscribe to registration data changes.
		// When registration completes (row created), re-check if we can set trid.
		this.registrationSub = this.claimsService.userRegistrationData$
			.pipe(filter((data) => data !== null))
			.subscribe(() => this.attemptTridFlow());

		// Also check immediately in case both conditions are already met (returning user)
		this.attemptTridFlow();
	}

	ngOnDestroy(): void {
		if (this.eventHandler) {
			window.removeEventListener('ccm19WidgetClosed', this.eventHandler);
		}
		this.registrationSub?.unsubscribe();
	}

	/**
	 * Attempts the trid flow only if BOTH conditions are met:
	 * 1. Consent for our cookie is given
	 * 2. User registration data exists
	 */
	private attemptTridFlow(): void {
		// Already resolved
		if (sessionStorage.getItem(TRID_SESSION_KEY)) return;

		// Condition 1: Consent given?
		if (!this.isFunctionalConsentGiven()) return;

		// Condition 2: Registration data exists?
		if (!this.claimsService.getCurrentUserRegistrationDataValue()) return;

		this.fetchOrCreateTrid();
	}

	/**
	 * Checks if the specific trid consent cookie has been accepted by the user.
	 * This cookie is registered in CCM19 under the "Functional" category.
	 * When the user accepts functional cookies, CCM19 allows this cookie to be set.
	 */
	private isFunctionalConsentGiven(): boolean {
		try {
			const ccm = window.CCM;
			if (!ccm) return false;

			// Check if our specific cookie is in the accepted cookies list
			if (ccm.acceptedCookies && Array.isArray(ccm.acceptedCookies)) {
				return ccm.acceptedCookies.some(
					(cookie: any) => cookie.name === TRID_CONSENT_COOKIE || cookie === TRID_CONSENT_COOKIE,
				);
			}

			// Fallback: check if the cookie already exists (CCM19 allowed it to be set)
			return document.cookie.includes(`${TRID_CONSENT_COOKIE}=`);
		} catch {
			return false;
		}
	}

	/**
	 * Tries to GET the existing trid from the API.
	 * If 404 (no trid column value), POSTs to generate a new one.
	 * Stores the result in sessionStorage.
	 */
	private fetchOrCreateTrid(): void {
		this.http
			.get<TridResponse>(`${this.environmentService.apiUrl}/user-registration/trid`)
			.pipe(
				take(1),
				catchError((error) => {
					if (error.status === 404) {
						// No trid set yet → generate one
						return this.http
							.post<TridResponse>(`${this.environmentService.apiUrl}/user-registration/trid`, {})
							.pipe(catchError(() => of(null)));
					}
					// Other errors → do nothing
					return of(null);
				}),
			)
			.subscribe((response) => {
				if (response?.trid) {
					sessionStorage.setItem(TRID_SESSION_KEY, response.trid);
				}
			});
	}

	/**
	 * Returns the current trid from sessionStorage, if available.
	 */
	getTrid(): string | null {
		return sessionStorage.getItem(TRID_SESSION_KEY);
	}
}
